import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { CapacityReservationsBoard, MaterialCatalogBoard, type CapacityReservationsBoardColumns } from '@api/BoardSDK';
import { readRateCardConfig } from '@generated/server/rate-card.server';
import { ALL_WEIGHT_FIELDS, resolveWeightColumn } from '@generated/server/material-columns.server';

// A public app has no per-user permission layer, so every text field is trimmed and the email is
// lowercased server-side — the trust boundary — regardless of what the client already did.
const trimmed = () => z.string().transform((v) => v.trim());
const trimmedRequired = () =>
  z
    .string()
    .transform((v) => v.trim())
    .pipe(z.string().min(1));

const materialInputSchema = z.object({
  id: z.string(),
  name: z.string(),
  mapsTo: z.string().nullable(),
  weightLbs: z.number(),
  requiresUpload: z.boolean(),
  fileNames: z.array(z.string()),
});
type SubmissionMaterial = z.infer<typeof materialInputSchema>;

const submitReservationSchema = z.object({
  submissionKey: z.string().min(1),
  company: z.object({
    company: trimmedRequired(),
    contactName: trimmedRequired(),
    email: z
      .string()
      .transform((v) => v.trim().toLowerCase())
      .pipe(z.email()),
    phone: trimmed(),
    shippingOrigin: trimmedRequired(),
  }),
  unit: z.enum(['lbs', 'tons']),
  materials: z.array(materialInputSchema),
  totalLbs: z.number(),
  termLbs: z.number(),
  formatId: z.string(),
  formatName: z.string(),
  frequency: z.string(),
  transportation: z.string(),
  commitment: z.string(),
  desiredShipmentDate: z.string(),
  unitsPerShipment: z.number(),
  fillWeightLbs: z.number(),
  periodCost: z.number().nullable(),
  annualCost: z.number().nullable(),
  signatoryName: trimmedRequired(),
  signatoryTitle: trimmedRequired(),
  signatureDate: z.string(),
  notes: z
    .string()
    .transform((v) => v.trim().slice(0, 2000)),
  // The rate/sizing values the client's current estimate was built on — compared against a fresh
  // read before any board write so a rate-card edit mid-session can never silently ship stale numbers.
  cachedFormat: z.object({
    nominalFillLbs: z.number().nullable(),
    volumetric: z.boolean(),
    palletRequired: z.boolean(),
    handlingPerUnit: z.number().nullable(),
    pricePerTon: z.number().nullable(),
    ratesEffective: z.string().nullable(),
  }),
  cachedGlobal: z.object({
    boxMaxMonthlyLbs: z.number().nullable(),
    volumetricFillMinLbs: z.number().nullable(),
    volumetricFillMaxLbs: z.number().nullable(),
  }),
});

function getPhoenixYear(): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Phoenix', year: 'numeric' }).formatToParts(
    new Date(),
  );
  const year = parts.find((p) => p.type === 'year')?.value;
  return year ? parseInt(year, 10) : new Date().getFullYear();
}

function buildWeightFields(materials: SubmissionMaterial[]): Record<string, number | null> {
  const fields: Record<string, number | null> = {};
  for (const key of ALL_WEIGHT_FIELDS) fields[key] = null;
  for (const m of materials) {
    const field = resolveWeightColumn(m.mapsTo);
    if (field) fields[field] = m.weightLbs;
  }
  return fields;
}

function buildMaterialFileMap(materials: SubmissionMaterial[]): string {
  const withFiles = materials.filter((m) => m.fileNames.length > 0);
  if (withFiles.length === 0) return 'No material-spec files attached.';
  return ['Material-spec file map:', ...withFiles.map((m) => `- ${m.name}: ${m.fileNames.join(', ')}`)].join('\n');
}

function money(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildCostBasisNote(
  format: { handlingPerUnit: number | null; pricePerTon: number | null; volumetric: boolean; ratesEffective: Date | null } | undefined,
  global: { boxMaxMonthlyLbs: number | null; volumetricFillMinLbs: number | null; volumetricFillMaxLbs: number | null },
  fillWeightLbs: number,
): string {
  if (!format) return 'Rate basis unavailable at submission time.';
  const lines: string[] = [];
  if (format.handlingPerUnit != null) lines.push(`Price per unit used: $${money(format.handlingPerUnit)}`);
  if (format.pricePerTon != null) lines.push(`Price per ton used: $${money(format.pricePerTon)}`);
  lines.push(`Fill weight used: ${Math.round(fillWeightLbs).toLocaleString()} lbs (${format.volumetric ? 'volumetric average' : 'nominal'})`);
  lines.push(`Rates effective: ${format.ratesEffective ? format.ratesEffective.toLocaleDateString('en-US') : 'not set'}`);
  if (global.boxMaxMonthlyLbs != null) lines.push(`Collection Box monthly ceiling used: ${global.boxMaxMonthlyLbs.toLocaleString()} lbs`);
  if (global.volumetricFillMinLbs != null) lines.push(`Volumetric fill minimum used: ${global.volumetricFillMinLbs.toLocaleString()} lbs`);
  if (global.volumetricFillMaxLbs != null) lines.push(`Volumetric fill maximum used: ${global.volumetricFillMaxLbs.toLocaleString()} lbs`);
  return lines.join('\n');
}

// Server-truth of which weight columns require an uploaded material-spec file — re-read from the
// Material Catalog board every time, never taken from anything the client sends. A client cannot
// spoof its way past the upload requirement by lying about which materials it selected.
async function getRequiredUploadWeightColumns(): Promise<Set<keyof CapacityReservationsBoardColumns>> {
  const catalog = new MaterialCatalogBoard();
  const res = await catalog
    .items()
    .withColumns(['requiresUpload', 'mapsTo', 'active'])
    .where({ active: true })
    .withPagination({ limit: 100 })
    .execute();
  const cols = new Set<keyof CapacityReservationsBoardColumns>();
  for (const it of res.items) {
    if (it.requiresUpload && it.mapsTo) {
      const col = resolveWeightColumn(it.mapsTo);
      if (col) cols.add(col);
    }
  }
  return cols;
}

async function findOrCreateItem(board: CapacityReservationsBoard, submissionKey: string, companyName: string) {
  const existing = await board
    .items()
    .withColumns(['submissionKey', 'referenceNumber'])
    .where({ submissionKey })
    .withPagination({ limit: 5 })
    .execute();
  if (existing.items.length > 0) {
    const item = existing.items[0];
    return { itemId: item.id, existingReference: item.referenceNumber ?? null };
  }
  const created = await board
    .item()
    .create({ name: companyName || 'New reservation', submissionKey })
    .returnColumns(['submissionKey'])
    .execute();
  return { itemId: created.id, existingReference: null as string | null };
}

async function findNextSequence(board: CapacityReservationsBoard, year: number): Promise<number> {
  const prefix = `BF-CAP-${year}-`;
  let max = 1000;
  let cursor: string | null | undefined;
  do {
    const res = await board
      .items()
      .withColumns(['referenceNumber'])
      .where({ referenceNumber: { contains: prefix } })
      .withPagination({ limit: 100, cursor: cursor ?? undefined })
      .execute();
    for (const it of res.items) {
      const ref = it.referenceNumber;
      const match = ref?.match(/-(\d{4})$/);
      if (match) {
        const seq = parseInt(match[1], 10);
        if (!Number.isNaN(seq) && seq > max) max = seq;
      }
    }
    cursor = res.cursor;
  } while (cursor);
  return max + 1;
}

// Simultaneous public submissions can race on the same candidate reference. We write the
// candidate only to OUR still-uncommitted item, then check for exact-match collisions: the item
// with the lowest monday item id wins the candidate, everyone else increments and retries.
async function resolveReferenceNumber(board: CapacityReservationsBoard, itemId: string): Promise<string> {
  const year = getPhoenixYear();
  let sequence = await findNextSequence(board, year);
  for (let attempt = 0; attempt < 10; attempt++) {
    const candidate = `BF-CAP-${year}-${String(sequence).padStart(4, '0')}`;
    await board.item(itemId).update({ referenceNumber: candidate }).execute();
    const matches = await board
      .items()
      .withColumns(['referenceNumber'])
      .where({ referenceNumber: candidate })
      .withPagination({ limit: 20 })
      .execute();
    if (matches.items.length <= 1) return candidate;
    const lowest = matches.items.reduce((min, it) => (Number(it.id) < Number(min.id) ? it : min));
    if (lowest.id === itemId) return candidate;
    sequence += 1;
  }
  throw new Error('REFERENCE_NOT_UNIQUE');
}

export const submitReservation = createServerFn({ method: 'POST' })
  .validator((d: unknown) => submitReservationSchema.parse(d))
  .handler(async ({ data }) => {
    const board = new CapacityReservationsBoard();

    // 1. Rate revalidation — compare the client's cached snapshot against a fresh read of every
    // Rate Card / Global setting field actually used to build the current estimate.
    const fresh = await readRateCardConfig();
    const freshFormat = fresh.formats.find((f) => f.id === data.formatId);
    const freshRatesIso = freshFormat?.ratesEffective ? freshFormat.ratesEffective.toISOString() : null;
    const rateChanged =
      !freshFormat ||
      freshFormat.nominalFillLbs !== data.cachedFormat.nominalFillLbs ||
      freshFormat.volumetric !== data.cachedFormat.volumetric ||
      freshFormat.palletRequired !== data.cachedFormat.palletRequired ||
      freshFormat.handlingPerUnit !== data.cachedFormat.handlingPerUnit ||
      freshFormat.pricePerTon !== data.cachedFormat.pricePerTon ||
      freshRatesIso !== data.cachedFormat.ratesEffective ||
      fresh.global.boxMaxMonthlyLbs !== data.cachedGlobal.boxMaxMonthlyLbs ||
      fresh.global.volumetricFillMinLbs !== data.cachedGlobal.volumetricFillMinLbs ||
      fresh.global.volumetricFillMaxLbs !== data.cachedGlobal.volumetricFillMaxLbs;

    if (rateChanged) {
      return { status: 'rate_changed' as const, global: fresh.global, formats: fresh.formats };
    }

    let itemId: string | undefined;
    try {
      const found = await findOrCreateItem(board, data.submissionKey, data.company.company);
      itemId = found.itemId;

      // A previously-assigned reference from an earlier attempt is only safe to reuse if it's
      // still uniquely ours right now — an earlier attempt could have raced another submission to
      // the same candidate and lost the tiebreak after this item already recorded it. Re-verify
      // rather than trusting the stored value, so a retry can never get permanently stuck
      // re-submitting a reference number it doesn't actually own.
      let referenceNumber = found.existingReference;
      if (referenceNumber) {
        const stillOwned = await board
          .items()
          .withColumns(['referenceNumber'])
          .where({ referenceNumber })
          .withPagination({ limit: 20 })
          .execute();
        const lowest =
          stillOwned.items.length > 0
            ? stillOwned.items.reduce((min, it) => (Number(it.id) < Number(min.id) ? it : min))
            : null;
        if (!lowest || lowest.id !== itemId) referenceNumber = null;
      }
      if (!referenceNumber) referenceNumber = await resolveReferenceNumber(board, itemId);

      const weightFields = buildWeightFields(data.materials);
      const costBasisNote = buildCostBasisNote(
        freshFormat ? { ...freshFormat, volumetric: freshFormat.volumetric ?? false } : undefined,
        fresh.global,
        data.fillWeightLbs,
      );
      const opsNotes = buildMaterialFileMap(data.materials);

      await board
        .item(itemId)
        .update({
          name: data.company.company,
          contactName: data.company.contactName,
          email: { email: data.company.email, label: 'Work' },
          phone: { phone: data.company.phone, country: 'US' },
          shippingOrigin: data.company.shippingOrigin,
          entryUnit: data.unit,
          ...weightFields,
          totalMonthlyWeight: data.totalLbs,
          termVolumeWeight: data.termLbs,
          format: data.formatName,
          frequency: data.frequency,
          transportation: data.transportation,
          term: data.commitment,
          desiredShipmentDate: new Date(data.desiredShipmentDate),
          unitsPerShipment: data.unitsPerShipment,
          fillWeightLbs: data.fillWeightLbs,
          estPeriodCost: data.periodCost,
          estAnnualCost: data.annualCost,
          signatoryName: data.signatoryName,
          signatoryTitle: data.signatoryTitle,
          signatureDate: new Date(data.signatureDate),
          authorizationConfirmed: true,
          submittedAt: new Date(),
          partnerNotes: data.notes || null,
          costBasisNote,
          opsNotes,
          submissionKey: data.submissionKey,
          referenceNumber,
        })
        .execute();

      // Status is intentionally left untouched (blank) — a later step finalizes the PDF and commit.
      return { status: 'ok' as const, itemId, referenceNumber, costBasisNote, ratesEffective: freshRatesIso };
    } catch (e) {
      console.error('Reservation submission failed', e);
      if (itemId) {
        try {
          await board
            .item(itemId)
            .update({
              opsNotes: `Submission attempt failed and was left incomplete (Status intentionally blank). Safe to retry — this item resumes automatically by Submission Key. Error: ${
                e instanceof Error ? e.message : String(e)
              }`,
            })
            .execute();
        } catch (noteError) {
          console.error('Could not record the submission failure on the item', noteError);
        }
      }
      throw new Error('SUBMISSION_FAILED');
    }
  });

const ALLOWED_UPLOAD_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// Re-checked server-side — the client already gates this, but a public app's server fn is invokable
// directly (e.g. from devtools) bypassing any client-side check entirely.
function assertAllowedUpload(file: File): void {
  const lower = file.name.toLowerCase();
  if (!ALLOWED_UPLOAD_EXTENSIONS.some((ext) => lower.endsWith(ext))) throw new Error('UNSUPPORTED_FILE_TYPE');
  if (file.size > MAX_UPLOAD_BYTES) throw new Error('FILE_TOO_LARGE');
}

// Item ids are small sequential numbers a public, unauthenticated client can guess or enumerate.
// The Submission Key (an unguessable UUID minted client-side on first Submit) is this app's only
// capability token — every mutating call that names an itemId must prove it holds the matching
// key for THAT item, or it's rejected. Without this check any caller could attach files to, or
// force the status commit of, another partner's reservation just by trying nearby item ids.
async function assertOwnsItem(board: CapacityReservationsBoard, itemId: string, submissionKey: string): Promise<void> {
  const check = await board.item(itemId).withColumns(['submissionKey']).execute();
  if (!check?.submissionKey || check.submissionKey !== submissionKey) {
    throw new Error('NOT_AUTHORIZED');
  }
}

export const uploadMaterialSpecFile = createServerFn({ method: 'POST' })
  .validator((data: FormData) => data)
  .handler(async ({ data }) => {
    const file = data.get('file') as File;
    const itemId = data.get('itemId') as string;
    const submissionKey = data.get('submissionKey') as string;
    if (!file || !itemId || !submissionKey) throw new Error('SUBMISSION_FAILED');
    assertAllowedUpload(file);
    const board = new CapacityReservationsBoard();
    try {
      await assertOwnsItem(board, itemId, submissionKey);
      // uploadFile's `columnId` is typed to the SDK's readable prop alias ("materialSpec"), but at
      // runtime it forwards that string as-is to the monday `add_file_to_column` mutation instead of
      // translating it to the board's real column id the way withColumns/create/update do — sending
      // the alias produces "Column not found" (confirmed via server logs). The raw id is what the
      // mutation actually needs, so it's cast past the (incorrectly narrow) type here.
      return await board.item(itemId).uploadFile({ columnId: 'file_mm75jywm', file } as any).execute();
    } catch (e) {
      // Never let a raw monday API exception (GraphQL error text, internal column ids, etc.) reach
      // the browser — log the full detail server-side for diagnosis and hand the client a clean,
      // safe-to-show message instead. This upload failing must NOT advance the submission: the
      // caller in App.tsx treats any thrown error here as a hard stop before finalize/Status.
      console.error('Material spec file upload failed', { itemId, fileName: file.name }, e);
      try {
        await board
          .item(itemId)
          .update({
            opsNotes: `A material-spec file upload failed and Status was left blank (safe to retry). File: "${file.name}". Error: ${
              e instanceof Error ? e.message : String(e)
            }`,
          })
          .execute();
      } catch (noteError) {
        console.error('Could not record the upload failure on the item', noteError);
      }
      throw new Error('UPLOAD_FAILED');
    }
  });

const REQUIRED_TEXT_FIELDS = [
  'contactName',
  'shippingOrigin',
  'format',
  'frequency',
  'transportation',
  'term',
  'signatoryName',
  'signatoryTitle',
] as const;

// Final commit — Prompt A5. Runs strictly AFTER the staged write in submitReservation: rechecks the
// finalized reference is still unique, uploads the canonical (blank ByFusion signature block) PDF to
// Signed PDF, verifies every required field / material-spec file / the PDF itself are all present —
// the upload requirement itself is re-derived from the Material Catalog board, not from anything the
// client claims — and ONLY THEN flips Status to "New Request", the single write board automations
// key off. Any failure leaves Status blank so the item resumes safely on retry.
export const finalizeReservation = createServerFn({ method: 'POST' })
  .validator((data: FormData) => data)
  .handler(async ({ data }) => {
    const itemId = data.get('itemId') as string;
    const referenceNumber = data.get('referenceNumber') as string;
    const pdfFile = data.get('pdf') as File;
    const submissionKey = data.get('submissionKey') as string;
    if (!itemId || !referenceNumber || !pdfFile || !submissionKey) throw new Error('FINALIZE_FAILED');

    const board = new CapacityReservationsBoard();

    try {
      // 0. Prove the caller holds the capability token for this exact item before touching it —
      // finalize is the call that flips Status, so this is the single most important place to
      // block a guessed/enumerated itemId from committing someone else's reservation.
      await assertOwnsItem(board, itemId, submissionKey);

      // 1. Reference must still be unique — a second submission could have raced past resolution.
      const matches = await board
        .items()
        .withColumns(['referenceNumber'])
        .where({ referenceNumber })
        .withPagination({ limit: 20 })
        .execute();
      if (matches.items.length === 0) throw new Error('REFERENCE_NOT_UNIQUE');
      const lowest = matches.items.reduce((min, it) => (Number(it.id) < Number(min.id) ? it : min));
      if (lowest.id !== itemId) throw new Error('REFERENCE_NOT_UNIQUE');

      // 2. Upload the canonical PDF, named with the finalized reference.
      const pdfName = `${referenceNumber} ByFusion capacity reservation.pdf`;
      const renamedPdf = new File([pdfFile], pdfName, { type: 'application/pdf' });
      // Same raw-column-id requirement as the material-spec upload above ("signedPdf" is the SDK
      // alias; "file_mm78sfkc" is the board's actual column id that add_file_to_column expects).
      // Cast past the type for the same reason: it's narrowed to the alias but the runtime needs
      // the raw id.
      await board.item(itemId).uploadFile({ columnId: 'file_mm78sfkc', file: renamedPdf } as any).execute();

      // 3. Verify everything required is actually present before the status commit.
      const requiredUploadColumns = await getRequiredUploadWeightColumns();
      const check = await board
        .item(itemId)
        .withColumns([
          ...REQUIRED_TEXT_FIELDS,
          ...ALL_WEIGHT_FIELDS,
          'email',
          'phone',
          'desiredShipmentDate',
          'signatureDate',
          'authorizationConfirmed',
          'totalMonthlyWeight',
          'materialSpec',
          'signedPdf',
          'submissionKey',
        ])
        .execute();

      const missing: string[] = [];
      for (const field of REQUIRED_TEXT_FIELDS) if (!check[field]) missing.push(field);
      if (!check.email) missing.push('email');
      if (!check.phone) missing.push('phone');
      if (!check.desiredShipmentDate) missing.push('desiredShipmentDate');
      if (!check.signatureDate) missing.push('signatureDate');
      if (!check.authorizationConfirmed) missing.push('authorizationConfirmed');
      if (!check.totalMonthlyWeight || check.totalMonthlyWeight <= 0) missing.push('totalMonthlyWeight');

      // A material that requires review is "in play" on this submission when its mapped weight
      // column actually carries a positive value on the item — derived purely from board data.
      const anyRequiredMaterialInPlay = Array.from(requiredUploadColumns).some((col) => {
        const value = check[col as keyof typeof check];
        return typeof value === 'number' && value > 0;
      });
      if (anyRequiredMaterialInPlay && (!check.materialSpec || check.materialSpec.length === 0)) {
        missing.push('materialSpec');
      }
      if (!check.signedPdf || check.signedPdf.length === 0) missing.push('signedPdf');
      if (missing.length > 0) throw new Error(`FINALIZE_INCOMPLETE:${missing.join(',')}`);

      // 4. Only now flip Status — board automations key off this exact change.
      await board.item(itemId).update({ status: 'New Request' }).execute();

      return { status: 'ok' as const, itemId, referenceNumber, pdfName };
    } catch (e) {
      console.error('Reservation finalize failed', e);
      try {
        await board
          .item(itemId)
          .update({
            opsNotes: `Finalize step failed and Status was left blank (safe to retry). Error: ${
              e instanceof Error ? e.message : String(e)
            }`,
          })
          .execute();
      } catch (noteError) {
        console.error('Could not record the finalize failure on the item', noteError);
      }
      throw new Error('FINALIZE_FAILED');
    }
  });
